import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Heatmap } from '../components/Heatmap';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { calculateStreaks, completionRate } from '../lib/streaks';
import { useTheme } from '../contexts/ThemeContext';
import { Footer } from '../components/Footer';
import * as LucideIcons from 'lucide-react';
import { Flame, Sun, Moon, ArrowRight, Sparkles } from 'lucide-react';

const toIconKey = (n) => (n || 'sparkles').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

export default function SharedHabit() {
  const { token } = useParams();
  const { theme, toggle } = useTheme();
  const [state, setState] = useState({ loading: true, habit: null, logs: [], notFound: false });

  useEffect(() => {
    (async () => {
      const { data: share } = await supabase
        .from('habit_shares')
        .select('habit_id')
        .eq('share_token', token)
        .maybeSingle();
      if (!share) { setState({ loading: false, habit: null, logs: [], notFound: true }); return; }
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const since = oneYearAgo.toISOString().slice(0, 10);
      const [{ data: habit }, { data: logs }] = await Promise.all([
        supabase.from('habits').select('*').eq('id', share.habit_id).maybeSingle(),
        supabase.from('habit_logs').select('*').eq('habit_id', share.habit_id).gte('log_date', since).order('log_date'),
      ]);
      setState({ loading: false, habit, logs: logs || [], notFound: !habit });
    })();
  }, [token]);

  if (state.loading) return (
    <div className="min-h-screen px-6 py-12 max-w-3xl mx-auto" data-testid="shared-loading">
      <Skeleton className="h-32 w-full rounded-2xl" />
      <Skeleton className="h-48 w-full rounded-2xl mt-4" />
    </div>
  );

  if (state.notFound) return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="shared-not-found">
      <div className="max-w-md text-center">
        <div className="text-primary mb-8 flex justify-center"><Logo /></div>
        <h1 className="font-heading text-2xl font-semibold">This link isn&apos;t available</h1>
        <p className="text-sm text-muted-foreground mt-2">It may have been revoked, or the link is incorrect.</p>
        <Link to="/" className="mt-6 inline-block">
          <Button className="rounded-full">Go to My Flow Habit</Button>
        </Link>
      </div>
    </div>
  );

  const { habit, logs } = state;
  const Icon = LucideIcons[toIconKey(habit.icon)] || LucideIcons.Sparkles;
  const streaks = calculateStreaks(habit, logs);
  const rate30 = completionRate(habit, logs, 30);
  const totalDone = logs.filter((l) => l.status === 'done').length;

  return (
    <div className="min-h-screen flex flex-col" data-testid="shared-page">
      <header className="flex items-center justify-between px-6 lg:px-12 py-6 pt-safe">
        <Link to="/" className="text-primary"><Logo size={44} /></Link>
        <button onClick={toggle} aria-label="Toggle theme" className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="shared-theme-toggle">
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-12 py-6 lg:py-10 space-y-8">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${habit.color}1F`, color: habit.color }}>
            <Icon className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">A flow worth sharing</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1 break-words" data-testid="shared-habit-name">{habit.name}</h1>
            {habit.description && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{habit.description}</p>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Current streak', value: streaks.current, icon: Flame, tone: 'text-secondary' },
            { label: 'Longest streak', value: streaks.longest, icon: Flame, tone: 'text-primary' },
            { label: '30-day completion', value: `${rate30}%`, icon: null },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card/30 p-5" data-testid={`shared-stat-${i}`}>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{s.label}</div>
              <div className="mt-2 flex items-center gap-2">
                {s.icon && <s.icon className={`h-5 w-5 ${s.tone}`} />}
                <div className="font-heading text-2xl font-semibold">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        <section className="rounded-2xl border border-border bg-card/30 p-5 sm:p-7">
          <h2 className="font-heading font-medium mb-4">Activity, last 12 months</h2>
          <Heatmap habit={habit} logs={logs} />
          <p className="text-xs text-muted-foreground mt-4">{totalDone} total check-ins so far.</p>
        </section>

        <section className="rounded-2xl border border-border bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <h3 className="font-heading font-medium">Build your own flow</h3>
              <p className="text-sm text-muted-foreground mt-1">My Flow Habit is a calmer way to track habits — free.</p>
            </div>
          </div>
          <Link to="/signup">
            <Button className="rounded-full" data-testid="shared-cta-signup">Start free <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
